import { useState, useEffect, useCallback } from 'react';
import {
  HiPlus, HiSearch, HiPencil, HiTrash, HiX,
  HiUsers, HiMail, HiPhone, HiOfficeBuilding,
  HiGlobeAlt, HiClipboardCopy, HiRefresh, HiLockClosed, HiLockOpen,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM = { name: '', email: '', phone: '', company: '', status: 'Active' };

const STATUS_STYLE = {
  Active:   'bg-green-100 text-green-700',
  Inactive: 'bg-gray-100  text-gray-500',
};

const AVATAR_COLORS = [
  'bg-indigo-500', 'bg-violet-500', 'bg-pink-500',   'bg-blue-500',
  'bg-emerald-500','bg-teal-500',   'bg-orange-500',  'bg-rose-500',
];

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

const avatarColor = (name) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function inputCls(hasError) {
  return [
    'w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition',
    hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300',
  ].join(' ');
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ─── Client Modal (Add / Edit) ────────────────────────────────────────────────

function ClientModal({ client, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(
    client
      ? { name: client.name, email: client.email, phone: client.phone ?? '', company: client.company ?? '', status: client.status }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      name:    form.name.trim(),
      email:   form.email.trim(),
      phone:   form.phone.trim()   || null,
      company: form.company.trim() || null,
      status:  form.status,
    };

    const { error } = client
      ? await supabase.from('clients').update(payload).eq('id', client.id).eq('user_id', user.id)
      : await supabase.from('clients').insert([{ ...payload, user_id: user.id }]);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success(client ? 'Client updated!' : 'Client added!');
      if (!client) {
        // Fire-and-forget welcome email for new clients
        fetch('/api/email/welcome-client', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientEmail: payload.email, clientName: payload.name }),
        }).catch(() => {});
      }
      onSaved();
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 animate-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {client ? 'Edit Client' : 'Add New Client'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">

          {/* Name */}
          <Field label="Full Name" required error={errors.name}>
            <div className="relative">
              <HiUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={form.name}
                onChange={set('name')}
                placeholder="John Smith"
                autoFocus
                className={inputCls(errors.name)}
              />
            </div>
          </Field>

          {/* Email */}
          <Field label="Email Address" required error={errors.email}>
            <div className="relative">
              <HiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="john@example.com"
                className={inputCls(errors.email)}
              />
            </div>
          </Field>

          {/* Phone + Company */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <div className="relative">
                <HiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+1 555 0100"
                  className={inputCls(false)}
                />
              </div>
            </Field>
            <Field label="Company">
              <div className="relative">
                <HiOfficeBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={form.company}
                  onChange={set('company')}
                  placeholder="Acme Corp"
                  className={inputCls(false)}
                />
              </div>
            </Field>
          </div>

          {/* Status */}
          <Field label="Status">
            <select
              value={form.status}
              onChange={set('status')}
              className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-xl text-sm
                text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                hover:border-gray-300 transition"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </Field>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white
              rounded-xl transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving
              ? <span className="flex items-center gap-2">
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Saving...
                </span>
              : client ? 'Save Changes' : 'Add Client'
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({ client, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-gray-100 p-6 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete Client</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Are you sure you want to delete{' '}
          <span className="font-semibold text-gray-800">{client.name}</span>?
          <br />This action cannot be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200
              hover:bg-gray-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white
              rounded-xl transition disabled:opacity-60"
          >
            {deleting ? 'Deleting...' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Portal Modal ─────────────────────────────────────────────────────────────

function PortalModal({ client, userId, onClose }) {
  const [portal,     setPortal]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState(false);
  const [toggling,   setToggling]   = useState(false);
  const [copied,     setCopied]     = useState(false);

  const portalUrl = portal
    ? `${window.location.origin}/portal/${portal.portal_token}`
    : '';

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('client_portals')
        .select('*')
        .eq('client_id', client.id)
        .eq('user_id', userId)
        .maybeSingle();
      setPortal(data ?? null);
      setLoading(false);
    };
    fetch();
  }, [client.id, userId]);

  const generatePortal = async (regenerate = false) => {
    setGenerating(true);
    const token = crypto.randomUUID();

    if (portal && !regenerate) { setGenerating(false); return; }

    const { data, error } = portal
      ? await supabase
          .from('client_portals')
          .update({ portal_token: token, is_active: true })
          .eq('id', portal.id)
          .select()
          .single()
      : await supabase
          .from('client_portals')
          .insert([{ user_id: userId, client_id: client.id, portal_token: token, is_active: true }])
          .select()
          .single();

    if (error) toast.error(error.message);
    else { setPortal(data); toast.success(regenerate ? 'Portal link regenerated!' : 'Portal created!'); }
    setGenerating(false);
  };

  const toggleActive = async () => {
    setToggling(true);
    const { data, error } = await supabase
      .from('client_portals')
      .update({ is_active: !portal.is_active })
      .eq('id', portal.id)
      .select()
      .single();
    if (error) toast.error(error.message);
    else { setPortal(data); toast.success(data.is_active ? 'Portal activated' : 'Portal deactivated'); }
    setToggling(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    toast.success('Portal link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-indigo-50 rounded-xl flex items-center justify-center">
              <HiGlobeAlt className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Client Portal</h2>
              <p className="text-xs text-gray-400">{client.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
            <HiX className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-6 w-6 text-indigo-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
          ) : !portal ? (
            /* No portal yet */
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <HiGlobeAlt className="w-7 h-7 text-indigo-400" />
              </div>
              <p className="text-sm font-semibold text-gray-800 mb-1">No portal yet</p>
              <p className="text-xs text-gray-400 mb-5">
                Generate a unique link so {client.name} can view their invoices, files, and contracts.
              </p>
              <button
                onClick={() => generatePortal(false)}
                disabled={generating}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5
                  rounded-xl transition text-sm disabled:opacity-60"
              >
                {generating ? 'Generating…' : 'Generate Portal Link'}
              </button>
            </div>
          ) : (
            /* Portal exists */
            <div className="space-y-4">

              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${portal.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-700">
                    {portal.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <button
                  onClick={toggleActive}
                  disabled={toggling}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition
                    ${portal.is_active
                      ? 'text-red-600 bg-red-50 hover:bg-red-100'
                      : 'text-green-600 bg-green-50 hover:bg-green-100'
                    } disabled:opacity-60`}
                >
                  {portal.is_active
                    ? <><HiLockClosed className="w-3.5 h-3.5" /> Deactivate</>
                    : <><HiLockOpen   className="w-3.5 h-3.5" /> Activate</>
                  }
                </button>
              </div>

              {/* Link */}
              {portal.is_active && (
                <>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                      Portal Link
                    </p>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="text-xs text-gray-600 truncate flex-1 font-mono">{portalUrl}</p>
                    </div>
                  </div>

                  <button
                    onClick={copyLink}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold
                      rounded-xl transition border
                      ${copied
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white border-transparent'
                      }`}
                  >
                    <HiClipboardCopy className="w-4 h-4" />
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </>
              )}

              {/* Regenerate */}
              <button
                onClick={() => generatePortal(true)}
                disabled={generating}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium
                  text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition disabled:opacity-60"
              >
                <HiRefresh className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Regenerating…' : 'Regenerate Link'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Loading Skeleton ─────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-6 py-4 animate-pulse">
      <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
      <div className="flex-1 space-y-2 min-w-0">
        <div className="h-3.5 bg-gray-200 rounded-full w-1/3" />
        <div className="h-3 bg-gray-100 rounded-full w-1/2" />
      </div>
      <div className="h-5 bg-gray-100 rounded-full w-16 hidden sm:block" />
      <div className="h-3 bg-gray-100 rounded-full w-20 hidden lg:block" />
      <div className="flex gap-1">
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [modalOpen, setModalOpen]         = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [portalClient, setPortalClient]   = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchClients = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) toast.error('Failed to load clients');
    else setClients(data ?? []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', deleteTarget.id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Client deleted');
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    }
    setDeleting(false);
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openAdd  = () => { setEditingClient(null); setModalOpen(true); };
  const openEdit = (c) => { setEditingClient(c);   setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingClient(null); };
  const onSaved  = () => { closeModal(); fetchClients(); };

  // ── Search filter ──────────────────────────────────────────────────────────

  const q = search.toLowerCase();
  const filtered = clients.filter((c) =>
    [c.name, c.email, c.company, c.phone].some((v) => (v ?? '').toLowerCase().includes(q))
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title="Clients">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Clients</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading...' : `${clients.length} client${clients.length !== 1 ? 's' : ''} total`}
          </p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white
            text-sm font-semibold px-4 py-2.5 rounded-xl transition shadow-sm self-start sm:self-auto"
        >
          <HiPlus className="w-4 h-4" />
          Add Client
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4.5 h-4.5" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or company…"
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm
            text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2
            focus:ring-indigo-500 focus:border-transparent"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Column headers — md+ */}
        {!loading && filtered.length > 0 && (
          <div className="hidden md:grid px-6 py-3 border-b border-gray-50 bg-gray-50/80
            grid-cols-[minmax(0,2fr)_minmax(0,2fr)_120px_140px_104px] gap-4">
            {['Client', 'Contact', 'Status', 'Added', 'Actions'].map((h) => (
              <p key={h} className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</p>
            ))}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="divide-y divide-gray-50">
            {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
          </div>

        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4">
              <HiUsers className="w-8 h-8 text-indigo-400" />
            </div>
            {search ? (
              <>
                <p className="text-sm font-semibold text-gray-700">No results for "{search}"</p>
                <p className="text-xs text-gray-400 mt-1">Try a different name, email, or company</p>
                <button onClick={() => setSearch('')} className="mt-3 text-xs text-indigo-600 font-medium hover:underline">
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700">No clients yet</p>
                <p className="text-xs text-gray-400 mt-1">Click "Add Client" to get started</p>
                <button
                  onClick={openAdd}
                  className="mt-4 inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700
                    text-white text-xs font-semibold px-4 py-2 rounded-xl transition"
                >
                  <HiPlus className="w-3.5 h-3.5" /> Add your first client
                </button>
              </>
            )}
          </div>

        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((client) => (
              <div
                key={client.id}
                className="flex md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_120px_140px_104px]
                  gap-3 md:gap-4 items-center px-6 py-4 hover:bg-gray-50/70 transition group"
              >
                {/* Client — name + company */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-full ${avatarColor(client.name)}
                    flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <span className="text-white font-bold text-sm">
                      {client.name[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{client.name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {client.company || <span className="text-gray-300">No company</span>}
                    </p>
                  </div>
                </div>

                {/* Contact — email + phone */}
                <div className="hidden md:block min-w-0">
                  <p className="text-sm text-gray-700 truncate">{client.email}</p>
                  <p className="text-xs text-gray-400">{client.phone || '—'}</p>
                </div>

                {/* Status */}
                <div className="hidden md:block">
                  <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full
                    ${STATUS_STYLE[client.status]}`}>
                    {client.status}
                  </span>
                </div>

                {/* Date */}
                <div className="hidden md:block">
                  <p className="text-xs text-gray-400">{formatDate(client.created_at)}</p>
                </div>

                {/* Mobile: status badge pushed right before actions */}
                <div className="flex md:hidden items-center gap-2 ml-auto flex-shrink-0">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[client.status]}`}>
                    {client.status}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0
                  md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setPortalClient(client)}
                    title="Client portal"
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                  >
                    <HiGlobeAlt className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEdit(client)}
                    title="Edit client"
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                  >
                    <HiPencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(client)}
                    title="Delete client"
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Result count when searching */}
      {search && !loading && filtered.length > 0 && (
        <p className="text-xs text-gray-400 mt-3 px-1">
          Showing {filtered.length} of {clients.length} clients
        </p>
      )}

      {/* Modals */}
      {modalOpen && (
        <ClientModal client={editingClient} onClose={closeModal} onSaved={onSaved} />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          client={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
      {portalClient && (
        <PortalModal
          client={portalClient}
          userId={user.id}
          onClose={() => setPortalClient(null)}
        />
      )}
    </Layout>
  );
}
